import React, { useState, useRef, useCallback, useEffect } from 'react';
import { searchTmdb } from '../../lib/tmdb.js';
import { useGameStore } from '../../store/game-store.js';
import './GuessInput.css';

const PLACEHOLDER_MESSAGES = {
  start: (targets) => {
    if (targets.length >= 2) {
      const names = targets.filter(t => t._resolvedName || t.name).map(t => t._resolvedName || t.name);
      if (names.length >= 2) return `Name a movie with ${names[0]} or ${names[1]}...`;
    }
    return 'Type an actor, movie, or show...';
  },
  exploring: 'Search any actor, movie, or show...',
  winning: 'One more link...',
  default: 'Type a guess...',
};

export default function GuessInput({ controller, disabled }) {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const inputRef = useRef(null);
  const searchTimer = useRef(null);

  const targets = useGameStore(s => s.targets);
  const won = useGameStore(s => s.won);
  const gameOptions = useGameStore(s => s.gameOptions);
  const addToast = useGameStore(s => s.addToast);

  const placeholder = gameOptions.isExploration
    ? PLACEHOLDER_MESSAGES.exploring
    : PLACEHOLDER_MESSAGES.start(targets);

  // Debounced autocomplete via TMDB search
  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setInput(val);
    setSelectedIdx(-1);

    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (val.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Check for hint shortcut
    if (val === '?') {
      setShowSuggestions(false);
      return;
    }

    searchTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchTmdb(val);
        const filtered = results
          .filter(r => r.media_type === 'person' || r.media_type === 'movie' || r.media_type === 'tv')
          .slice(0, 8)
          .map(r => ({
            id: r.id,
            type: r.media_type,
            label: r.name || r.title,
            year: r.release_date?.split('-')[0] || r.first_air_date?.split('-')[0] || '',
          }));
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      } catch {
        setSuggestions([]);
      }
      setLoading(false);
    }, 200);
  }, []);

  const handleSubmit = useCallback(async (guessText) => {
    if (!controller || !guessText?.trim()) return;

    setInput('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIdx(-1);

    const result = await controller.processGuess(guessText);

    if (!result.success) {
      const messages = {
        'no-match': `Good thinking, but "${guessText}" doesn't match anything in the pool yet.`,
        'no-connection': `"${result.label || guessText}" doesn't connect to anyone on your board yet.`,
        'already-found': `${result.label || guessText} is already on the board.`,
        'banned': `${guessText} is on the ban list.`,
        'constraint': `${result.label || guessText} doesn't meet the current constraints.`,
        'empty': '',
      };
      const msg = messages[result.reason] || `No match found for "${guessText}".`;
      if (msg) addToast(msg, 'error');
    }
  }, [controller, addToast]);

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx(prev => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx(prev => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedIdx >= 0) {
          handleSubmit(suggestions[selectedIdx].label);
        } else {
          handleSubmit(input);
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSelectedIdx(-1);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(input);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSubmit(suggestion.label);
  };

  // Auto-focus on mount
  useEffect(() => {
    if (inputRef.current && window.innerWidth >= 768) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div className="guess-input">
      <div className="guess-input__wrap">
        <input
          ref={inputRef}
          className="guess-input__field"
          type="text"
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck="false"
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        />
        {loading && <span className="guess-input__spinner" />}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="guess-input__suggestions">
          {suggestions.map((s, i) => (
            <li
              key={`${s.type}-${s.id}`}
              className={`guess-input__suggestion ${i === selectedIdx ? 'guess-input__suggestion--selected' : ''}`}
              onMouseDown={() => handleSuggestionClick(s)}
            >
              <span className={`guess-input__suggestion-type guess-input__suggestion-type--${s.type}`}>
                {s.type === 'person' ? 'Actor' : s.type === 'movie' ? 'Film' : 'TV'}
              </span>
              <span className="guess-input__suggestion-label">{s.label}</span>
              {s.year && <span className="guess-input__suggestion-year">{s.year}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
